import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IAlertActivation, defaultValue } from 'app/shared/model/alert-activation.model';
import { IInfoRoutesByCompany, IInfoRoutesByTruckImplement } from 'app/modules/dashboards/dashboard-truck-implement/interface/dashboardTruckImplementInterface';

const initialState: EntityState<IAlertActivation> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/alert-activations';

// Actions

export const getEntities = createAsyncThunk('alertActivation/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IAlertActivation[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'alertActivation/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IAlertActivation>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'alertActivation/create_entity',
  async (entity: IAlertActivation, thunkAPI) => {
    const result = await axios.post<IAlertActivation>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'alertActivation/update_entity',
  async (entity: IAlertActivation, thunkAPI) => {
    const result = await axios.put<IAlertActivation>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'alertActivation/partial_update_entity',
  async (entity: IAlertActivation, thunkAPI) => {
    const result = await axios.patch<IAlertActivation>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'alertActivation/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IAlertActivation>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getInfractionsByTruckImplement = createAsyncThunk(
  'alertActivation/fetch_infractions_by_truck_implement',
  async ({ page, size, sort, truckImplementId, dateStartPeriodic, dateEndPeriodic, nameSearch }: IInfoRoutesByTruckImplement) => {
    return axios.get<IAlertActivation[]>(
      `${apiUrl}${`/truck-implement/${truckImplementId}/${dateStartPeriodic}/${dateEndPeriodic}?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&cacheBuster=${new Date().getTime()}`}`
    );
  },
  { serializeError: serializeAxiosError }
);

export const getInfractionsByTruckImplementAndPeriodic = createAsyncThunk(
  'alertActivation/fetch_infractions_by_truck_implement_and_periodic',
  async ({ page, size, sort, truckImplementId, periodicType, nameSearch }: IInfoRoutesByTruckImplement) => {
    return axios.get<IAlertActivation[]>(
      `${apiUrl}${`/truck-implement/${truckImplementId}/${periodicType}?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&cacheBuster=${new Date().getTime()}`}`
    );
  },
  { serializeError: serializeAxiosError }
);

export const getSensorAlertsActivationByCompanyAndDateStartAndDateEnd = createAsyncThunk(
  'alertActivation/fetch_infractions_by_company_and_date_start_and_date_end',
  async ({ page, size, sort, dateStartPeriodic, dateEndPeriodic, nameSearch, licensePlateSearch, searchCompanyId, truckImplementId }: IInfoRoutesByCompany) => {
    return axios.get<IAlertActivation[]>(
      `${apiUrl}${`/company/${dateStartPeriodic}/${dateEndPeriodic}?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&licensePlateSearch=${licensePlateSearch}&cacheBuster=${new Date().getTime()}`}`,
      { params: { searchCompanyId, truckImplementId } }
    );
  },
  { serializeError: serializeAxiosError }
);

export const getSensorAlertsActivationByCompanyAndPeriodic = createAsyncThunk(
  'alertActivation/fetch_infractions_by_company_and_periodic',
  async ({ page, size, sort, periodicType, nameSearch, licensePlateSearch, searchCompanyId, truckImplementId }: IInfoRoutesByCompany) => {
    return axios.get<IAlertActivation[]>(
      `${apiUrl}${`/company/${periodicType}?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&licensePlateSearch=${licensePlateSearch}&cacheBuster=${new Date().getTime()}`}`,
      { params: { searchCompanyId, truckImplementId } }
    );
  },
  { serializeError: serializeAxiosError }
);

// slice

export const AlertActivationSlice = createEntitySlice({
  name: 'alertActivation',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getInfractionsByTruckImplement), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getInfractionsByTruckImplementAndPeriodic), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getSensorAlertsActivationByCompanyAndPeriodic), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getSensorAlertsActivationByCompanyAndDateStartAndDateEnd), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(
        isPending(
          getEntities,
          getEntity,
          getInfractionsByTruckImplement,
          getInfractionsByTruckImplementAndPeriodic,
          getSensorAlertsActivationByCompanyAndDateStartAndDateEnd,
          getSensorAlertsActivationByCompanyAndPeriodic
        ),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.loading = true;
        }
      )
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = AlertActivationSlice.actions;

// Reducer
export default AlertActivationSlice.reducer;
